import React from 'react'
import { AlertTypeEnums } from '../../common/constants'

interface AlertControlsSectionProps {
  advancedMode: boolean
  alertControls: { [alertType: string]: boolean }
  onAlertControlChange: (alertType: string, enabled: boolean) => void
  disabled?: boolean
}

export function AlertControlsSection({ advancedMode, alertControls, onAlertControlChange, disabled }: AlertControlsSectionProps) {
  return (
    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
      <h3 className="text-sm font-medium text-gray-900 mb-4">Alert Types</h3>

      <div className="space-y-4">
        {/* Sensor Alerts - Always visible */}
        <div className="flex items-center justify-between">
          <div>
            <label htmlFor="sensor-alerts" className="text-sm font-medium text-gray-700">
              Sensor Alerts
            </label>
            <p className="text-xs text-gray-500">Receive alerts when sensors detect issues</p>
          </div>
          <input
            type="checkbox"
            id="sensor-alerts"
            checked={alertControls[AlertTypeEnums.SENSOR_ALERTS] ?? false}
            onChange={(e) => onAlertControlChange(AlertTypeEnums.SENSOR_ALERTS, e.target.checked)}
            disabled={disabled}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Battery Alerts - Always visible */}
        <div className="flex items-center justify-between">
          <div>
            <label htmlFor="battery-alerts" className="text-sm font-medium text-gray-700">
              Battery Alerts
            </label>
            <p className="text-xs text-gray-500">Receive alerts for low battery and power loss</p>
          </div>
          <input
            type="checkbox"
            id="battery-alerts"
            checked={alertControls[AlertTypeEnums.BATTERY_LOW] ?? false}
            onChange={(e) => onAlertControlChange(AlertTypeEnums.BATTERY_LOW, e.target.checked)}
            disabled={disabled}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Offline Alerts - Always visible */}
        <div className="flex items-center justify-between">
          <div>
            <label htmlFor="offline-alerts" className="text-sm font-medium text-gray-700">
              Offline Alerts
            </label>
            <p className="text-xs text-gray-500">Receive alerts when devices go offline</p>
          </div>
          <input
            type="checkbox"
            id="offline-alerts"
            checked={alertControls[AlertTypeEnums.DEVICE_OFFLINE] ?? false}
            onChange={(e) => onAlertControlChange(AlertTypeEnums.DEVICE_OFFLINE, e.target.checked)}
            disabled={disabled}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* System Messages - Advanced mode only */}
        {advancedMode && (
          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="system-messages" className="text-sm font-medium text-gray-700">
                System Messages
              </label>
              <p className="text-xs text-gray-500">Receive system control and status messages</p>
            </div>
            <input
              type="checkbox"
              id="system-messages"
              checked={alertControls[AlertTypeEnums.SYSTEM_CONTROL_MESSAGES] ?? false}
              onChange={(e) => onAlertControlChange(AlertTypeEnums.SYSTEM_CONTROL_MESSAGES, e.target.checked)}
              disabled={disabled}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        )}

        {/* Test Messages - Advanced mode only */}
        {advancedMode && (
          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="test-messages" className="text-sm font-medium text-gray-700">
                Test Messages
              </label>
              <p className="text-xs text-gray-500">Receive test notifications for verification</p>
            </div>
            <input
              type="checkbox"
              id="test-messages"
              checked={alertControls[AlertTypeEnums.TEST_MESSAGES] ?? false}
              onChange={(e) => onAlertControlChange(AlertTypeEnums.TEST_MESSAGES, e.target.checked)}
              disabled={disabled}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        )}
      </div>
    </div>
  )
}
